import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Determine environment: use VITE_APP_ENV if set, otherwise fall back to MODE
// This allows staging and production to be properly distinguished
const getEnvironment = () => {
  const appEnv = import.meta.env.VITE_APP_ENV;
  if (appEnv) return appEnv;

  // Fallback to MODE for local development
  const mode = import.meta.env.MODE;
  return mode === 'development' ? 'development' : 'production';
};

export const initSentry = () => {
  if (SENTRY_DSN) {
    const environment = getEnvironment();

    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        // Performance Monitoring (Tracing)
        Sentry.browserTracingIntegration(),
        // Session Replay with privacy controls
        Sentry.replayIntegration({
          // Mask sensitive text by default
          maskAllText: true,
          // Block media to reduce payload size and privacy
          blockAllMedia: true,
          // Unmask specific elements if needed (add data-sentry-unmask attribute)
          maskAllInputs: true,
          // Network details
          networkDetailAllowUrls: [
            /^https:\/\/.*\.supabase\.co/,
            window.location.origin,
          ],
        }),
        // Console breadcrumbs - only in development
        Sentry.breadcrumbsIntegration({
          console: environment === 'development',
        }),
      ],

      // Performance Monitoring (Tracing)
      // MVP: 100% sampling to catch all performance issues with small user base
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.supabase\.co/,
        /^https:\/\/.*\.vercel\.app/,
        /^https:\/\/brokers\.smb\.co/,
        /^https:\/\/advisors\.smb\.co/,
      ],

      // Session Replay sampling
      // MVP: High sampling rates for better debugging
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 0.2, // 10% prod, 20% staging - for UX research
      replaysOnErrorSampleRate: 1.0, // 100% error replays - always want to see what happened

      // Environment
      environment,

      // Privacy - don't send PII by default
      sendDefaultPii: false,

      // Filter sensitive data before sending
      beforeSend(event, hint) {
        // Remove sensitive headers
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
          delete event.request.headers['X-API-Key'];
        }

        // Scrub sensitive query params
        if (event.request?.url) {
          const url = new URL(event.request.url);
          ['token', 'api_key', 'apikey', 'password', 'secret'].forEach(param => {
            if (url.searchParams.has(param)) {
              url.searchParams.set(param, '[Filtered]');
            }
          });
          event.request.url = url.toString();
        }

        // In development, log errors to console too
        if (environment === 'development') {
          console.error('Sentry captured error:', hint.originalException || hint.syntheticException);
        }

        return event;
      },

      // Ignore only truly non-actionable errors
      // MVP: Keep filtering minimal to catch real bugs
      ignoreErrors: [
        // Browser extensions - not our code
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // ResizeObserver errors - browser quirk, completely benign
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // PostHog client errors - third-party library
        /posthog/i,
        // Known ad blocker interference
        'AbortError: The operation was aborted',
      ],

      // Ignore errors from third-party scripts
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
        /analytics\.js/i,
      ],
    });

    console.log(`Sentry initialized for ${environment} environment`);
  } else {
    console.warn('Sentry DSN not found. Error tracking will be disabled.');
  }
};

// Helper: Add this attribute to elements you want visible in Sentry replays
// Example: <div data-sentry-unmask>Safe to view</div>
export const SENTRY_UNMASK_ATTR = 'data-sentry-unmask';

export { Sentry };
